import { createFileRoute, notFound, useNavigate, useParams } from '@tanstack/react-router';
import { ApiError } from '@/lib/api-client';
import { lazy, Suspense, useRef, useState } from 'react';
import { CircleHelp, MessageSquare, ShieldCheck, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { SizedDialog } from '@/components/SizedDialog';
import { VaultDocumentPage, documentIdentityFields } from '@/features/vault/components/VaultDocumentPage';
import { VersionDiff, type FileLinks } from '@/features/vault/components/VersionDiff';
import { ThreadAvatar, ThreadComposer } from '@/features/vault/components/ReviewThread';
import { DiscussionSheet } from '@/features/vault/components/DiscussionSheet';
import { versionDownloadUrl } from '@/features/vault/collab';
import { toastMutationError } from '@/lib/mutationError';
import { cn } from '@/lib/utils';
import { useMe } from '@/features/auth/hooks';
import { useVaultDocument } from '@/features/vault/hooks';
import { canEditVaultDoc, canReviewVaultDoc } from '@/features/vault/lib/reviewRbac';
import { isProseType, sourceLabel, sourceTypeOf } from '@/features/vault/lib/sourceMeta';
import { CharacterCount } from '@/components/CharacterCount';
import { ClampedText } from '@/components/ClampedText';
import { COLLAB_MESSAGE_MAX } from '@/features/vault/limits';
import {
  useApproveDraft,
  useCommentDraft,
  useDecideChange,
  useLockWatcher,
  useRejectDraft,
  useReplyDraft,
  useReviewDecisions,
  useVaultDraft,
  useVaultDraftDiff,
  useVaultVersions,
} from '@/features/vault/collabHooks';
import { REVIEW_WAIT_PROBE_MS, REVIEW_WAIT_SETTLE_MS } from '@/features/vault/lockState';
import { changeLabel, changePreview, changeTextForRecord, weldedIds, isPictureChange, reviewProgress, type ChangeEntry, type DecisionKind } from '@/features/vault/reviewDecisions';
import type { CommentTarget } from '@/features/vault/collabTypes';

export const Route = createFileRoute('/vault_/document/$ref/review')({ component: ReviewPage });

/** Separation of duties, in the reviewer's words. The server refuses the same actions. */
const OWN_EDIT_NOTE = 'You made these edits — a different reviewer must review and sign them.';

// Word redline (read-only 'viewing' mode) — lazy so the heavy docx engine only loads
// when reviewing a docx-backed document.
const DocxRedline = lazy(() => import('@/features/vault/components/docx/DocxRedline'));


/** On-demand help — how the review works lives in a popover, not a big box up top
 *  (mirrors the edit page's "How editing works"). Content adapts to the viewer's role. */
function HowReviewWorks({ canReview, isProse }: { canReview: boolean; isProse: boolean }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="text-muted hover:text-navy">
          <CircleHelp className="size-4" /> How review works
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[min(24rem,calc(100vw-2rem))]">
        <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.06em] text-muted">
          How review works
        </p>
        {canReview ? (
          <ul className="space-y-2.5 text-[12.5px] leading-relaxed text-muted">
            <li>
              <span className="font-semibold text-navy">Approve or send back the whole revision.</span>{' '}
              Approving e-signs it as the next released version; sending it back returns it to the editor
              with your comments.
            </li>
            <li>
              <span className="font-semibold text-navy">Comment on any change.</span> Leave a comment on a
              specific change to tell the editor what to fix — your comments travel with a send-back.
            </li>
            {!isProse && (
              <li>
                <span className="font-semibold text-navy">Citations are verified.</span> They&apos;re
                checked automatically and can&apos;t be changed.
              </li>
            )}
          </ul>
        ) : (
          <ul className="space-y-2.5 text-[12.5px] leading-relaxed text-muted">
            <li>
              <span className="font-semibold text-navy">Submitted for review.</span> A reviewer will
              either approve these changes — releasing the next version — or send them back for you to
              revise.
            </li>
            <li>
              <span className="font-semibold text-navy">Read-only for now.</span> Below is a read-only
              view of what you sent. You can reply to the reviewer in the discussion.
            </li>
          </ul>
        )}
      </PopoverContent>
    </Popover>
  );
}

/** Exported for its test: createFileRoute is mocked there, so Route is a stub. */
export function ReviewPage() {
  const { ref } = useParams({ from: '/vault_/document/$ref/review' });
  const navigate = useNavigate();
  const me = useMe();
  const canReview = canReviewVaultDoc(me.data?.roles);
  const canEdit = canEditVaultDoc(me.data?.roles);
  const detail = useVaultDocument(ref);
  const versions = useVaultVersions(ref);
  const approve = useApproveDraft();
  const reject = useRejectDraft();
  const comment = useCommentDraft();
  const replyM = useReplyDraft();
  const [discussionOpen, setDiscussionOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [approveReason, setApproveReason] = useState('');
  const [sendBackReason, setSendBackReason] = useState('');
  const [approveOpen, setApproveOpen] = useState(false);
  const [sendBackOpen, setSendBackOpen] = useState(false);
  // Which change a comment is about (set by the rail's Comment button; cleared after posting).
  // The unsent message, kept across closing the drawer (which unmounts it). A ref, so
  // typing never re-renders this page — see ThreadComposer.
  const threadDraft = useRef('');
  const [commentTarget, setCommentTarget] = useState<CommentTarget | null>(null);

  const productName = detail.data?.product_name ?? 'Working copy';
  const sourceType = sourceTypeOf(ref);
  const country = ref.split(':')[2] ?? null;
  const typeLabel = sourceLabel(sourceType);
  const isProse = isProseType(sourceType);
  const draft = versions.data?.working_draft ?? null;
  const released = versions.data?.released ?? null;
  const inReview = draft?.state === 'in_review';
  // The persistent review thread lives on the draft payload (audit-backed).
  // Live while the review is open: the reviewer and the editor are talking to each other.
  // The conversation outlives the review state: after a send-back the copy is a draft, the
  // editor is answering, and a reviewer who cannot read or reply until it is resubmitted turns
  // a conversation into two monologues. Fetched (and polled) whenever a copy is open.
  const hasOpenCopy = !!draft;
  // ...but only once there is something to show THIS person. A copy the editor has checked out
  // and not yet submitted is unfinished work: the server refuses it, and asking anyway drew the
  // whole document under a line reading "Submitted for review by <the editor>", which was
  // untrue — they had not submitted (2026-10-09). The server decides and says so in
  // `readable_by_me`; the rule is not restated here, so the two cannot drift.
  const mayReadCopy = !!draft?.readable_by_me;
  // Only once there is a copy this person may read. Asked any earlier the request fails — there
  // is nothing to compare, or the server refuses an unsubmitted copy — and the failure is
  // CACHED, so when the copy did arrive the page updated but went on saying "Couldn't load the
  // changes" until somebody reloaded. That is the very thing the watcher below exists to avoid
  // (two-browser run, 2026-10-09). The lock watcher also clears this query now, for a copy that
  // appears some other way.
  const diff = useVaultDraftDiff(ref, mayReadCopy);
  const entries = diff.data ?? [];
  // A FAILED comparison is not an empty one. `diff.data` is undefined either way, so without
  // this the reviewer was told "this working copy is identical to the released version —
  // nothing to approve, send it back" when the truth was that we could not fetch the changes.
  // Sending back a real set of edits is the wrong end of that mistake.
  // Deliberately TRUE when the comparison failed as well as when it found nothing, because
  // this flag also gates Approve (below). A reviewer must not be able to e-sign an approval
  // for changes we could not load. The MESSAGE distinguishes the two cases — see the
  // `diff.isError` branch, which is checked first — but the approval gate must not.
  const noChanges = !diff.isPending && entries.length === 0;
  // A working copy appearing while the reviewer is ALREADY on this page now shows up within
  // seconds, without them reloading.
  //
  // It did not before: "Nothing to review" comes from useVaultVersions, which never polls and
  // does not refetch on focus (the app turns that off globally), and useVaultDraft — which does
  // poll — is switched off precisely when there is NO open copy, so it could not rescue it. A
  // reviewer who opened this page before the editor submitted sat watching nothing happen until
  // somebody told them to reload (staging, 2026-10-07). The document page has always had this
  // watcher; this page never did. It polls the cheap lock probe and invalidates the versions
  // query when the signature moves — and the signature carries the copy's STATE, so a submit
  // moves it.
  //
  // Until the copy is IN REVIEW, not just until one exists. The first version watched only the
  // step from "no copy" to "a copy" — but the normal flow is check out, edit, then submit later,
  // so a reviewer who opened this page while the editor still had a draft open never saw the
  // submit and was back to reloading. Same symptom, one step further along (QA audit P2).
  //
  // It stops once the copy is in review: from there useVaultDraft below polls, and a second
  // probe on the same page would be steady load for no answer. That probe is the vault's single
  // biggest source of it (lockState.ts), so it is spent where it buys something and nowhere else.
  // Fast, because this is the one screen where somebody is actively waiting for it to change
  // — and only until the copy is in review, when this stops and the page's own polling
  // takes over. See REVIEW_WAIT_PROBE_MS for why that bounds the cost.
  useLockWatcher(inReview ? null : ref, REVIEW_WAIT_PROBE_MS, REVIEW_WAIT_SETTLE_MS);
  const draftContent = useVaultDraft(ref, mayReadCopy, mayReadCopy && (discussionOpen ? 'open' : true));
  const thread = draftContent.data?.review_thread ?? [];
  const showThread = thread.length > 0 || canReview || canEdit;
  // Per-change decisions (docx redlines only): recorded on the server, one Activity row each.
  // Rule 1 — Approve only when every change is accepted and none rejected — is mirrored here so
  // the buttons tell the truth before the round trip; the server enforces it too.
  const isDocxDraft = !!draft?.filename?.toLowerCase().endsWith('.docx');
  // Separation of duties: whoever made the edits reads the review, never performs it.
  const ownEdit = draft?.edited_by_me === true;
  const mayReview = canReview && !ownEdit;
  // The editor watches the decisions land too — their rail is read-only, not blind.
  const decisionsQ = useReviewDecisions(ref, inReview && isDocxDraft && (canReview || canEdit));
  const decide = useDecideChange();
  const decisionsPage = decisionsQ.data;
  const progress = decisionsPage ? reviewProgress(decisionsPage.revision_ids, decisionsPage.decisions) : null;
  const rejectedDecisions = decisionsPage?.decisions.filter((d) => d.decision === 'reject') ?? [];
  // Discussion comments per change, for the rail's cards.
  const commentCounts: Record<number, number> = {};
  for (const e of thread) {
    if (e.kind === 'comment' && e.revision_id !== null) commentCounts[e.revision_id] = (commentCounts[e.revision_id] ?? 0) + 1;
  }
  const decideChange = (entry: ChangeEntry, decision: DecisionKind | null) =>
    decide.mutate(
      {
        ref,
        revisionId: entry.revisionId,
        body: {
          decision,
          insertion_revision_id: entry.insertionRevisionId ?? null,
          // WELDED ids only — the two halves of a replacement, which are one edit and cannot be
          // decided apart. Deciding only the primary left the insertion half with no row, so
          // approval refused with nothing left to click (QA audit, 2026-10-09).
          //
          // NOT the bundled ones. The library also joins edits that merely share an author and a
          // minute — a new paragraph elsewhere in the document — and those get their own verdict
          // below. Carrying them here meant a reviewer who liked the word swap but not the new
          // paragraph had no way to say so.
          covers: weldedIds(entry),
          change_type: entry.type,
          change_text: changeTextForRecord(entry),
        },
      },
      { onError: (e) => toastMutationError(e, decision ? 'Could not record the decision' : 'Could not undo') },
    );

  // A missing / cross-tenant ref 404s → the standalone bare 404 page (like a plain bad
  // URL), so a shared link to another tenant's document NEVER flashes vault chrome or a
  // soft "nothing to review". Mirrors DocumentDetail's guard. (The backend already 404s
  // cross-tenant refs — no existence leak; this just makes the client render it cleanly.)
  const is404 = (q: { isError: boolean; error: unknown }) =>
    q.isError && q.error instanceof ApiError && q.error.status === 404;
  if (is404(detail) || is404(versions) || (!detail.isPending && !detail.isError && !detail.data)) {
    throw notFound();
  }
  // Until the document is confirmed to exist, show a neutral loader — no hero / review
  // UI — so nothing document-specific flashes before a 404 resolves.
  if (detail.isPending || versions.isPending) {
    return (
      <main className="mx-auto max-w-[1280px] px-4 pt-8 pb-20 sm:px-6 lg:px-10">
        <p className="rounded-2xl border border-line bg-white px-6 py-16 text-center text-[13px] text-muted shadow-card">
          Loading…
        </p>
      </main>
    );
  }

  // Docx-backed documents (prose + uploads) are reviewed as a native Word redline,
  // not the field/file diff cards.
  const isDocx = !!draft?.filename?.toLowerCase().endsWith('.docx');

  // File-backed (upload) review: download/preview links for the released base file vs the
  // working-copy file. View is ALWAYS offered and opens our own viewer — the same rule as
  // every other screen. It used to be hidden for anything that was not a PDF, and to open a
  // browser tab when it was shown, so a reviewer comparing two images had no way to look at
  // either without downloading them.
  const fileLinks: FileLinks | undefined =
    sourceType === 'upload' && released && draft
      ? {
          current: {
            download: versionDownloadUrl(ref, released.version_id),
            view: versionDownloadUrl(ref, released.version_id, true),
            filename: released.filename,
          },
          proposed: {
            download: versionDownloadUrl(ref, draft.version_id),
            view: versionDownloadUrl(ref, draft.version_id, true),
            filename: draft.filename,
          },
        }
      : undefined;

  // Discussion comments about a FIELD change, keyed by the words the card showed — the field
  // label is what the comment was tagged with.
  const fieldCommentCounts: Record<string, number> = {};
  for (const e of thread) {
    if (e.kind === 'comment' && e.revision_id === null && e.change_text) {
      const hit = entries.find((x) => x.field_label === e.change_text);
      if (hit) fieldCommentCounts[hit.path] = (fieldCommentCounts[hit.path] ?? 0) + 1;
    }
  }
  const commentCount = Object.values(fieldCommentCounts).reduce((a, b) => a + b, 0);
  // What this revision changes, for a document with no tracked changes to count.
  const fileEntry = entries.find((e) => e.kind === 'file');
  const revisionSummaryText = fileEntry
    ? `The file · ${fileEntry.old ?? 'none'} → ${fileEntry.new ?? 'none'}`
    : entries.length > 0
      ? `${entries.length} field${entries.length === 1 ? '' : 's'} changed`
      : 'nothing changed';
  // The reason is REQUIRED, matching the lifecycle signature (SignatureDialog). Both are
  // signatures under 21 CFR 11, and 11.50 wants the meaning of one recorded — a blank reason
  // here is the same omission whichever button produced it. Trimmed, so whitespace isn't a
  // reason.
  // A docx with tracked changes also needs every change accepted and none rejected (rule 1).
  const decisionsBlockApproval = isDocx && progress !== null && progress.total > 0 && !progress.canApprove;
  const approvalBlocker = !decisionsBlockApproval || !progress
    ? null
    : progress.rejected > 0
      ? `${progress.rejected} change${progress.rejected === 1 ? ' is' : 's are'} rejected — send the copy back instead.`
      : `${progress.open} change${progress.open === 1 ? ' has' : 's have'} not been accepted yet.`;
  const canApprove =
    !noChanges && !decisionsBlockApproval && !!password && approveReason.trim().length > 0 && !approve.isPending;
  const canSendBack = !!sendBackReason.trim() && !reject.isPending;

  const goBack = () => navigate({ to: '/vault/document/$ref', params: { ref } });

  const approveNow = () =>
    approve.mutate(
      { ref, password, reason: approveReason.trim() },
      {
        onSuccess: () => {
          toast.success('Approved & released.');
          goBack();
        },
        onError: (e) => toastMutationError(e, 'Approve failed'),
      },
    );

  const sendBack = () =>
    reject.mutate(
      { ref, reason: sendBackReason },
      {
        onSuccess: () => {
          toast.success('Sent back to the editor.');
          goBack();
        },
        onError: (e) => toastMutationError(e, 'Send back failed'),
      },
    );

  // The composer holds the half-written message and hands it over here; it clears itself once
  // this resolves, and keeps the text if it does not.
  const postComment = (body: string) =>
    comment
      .mutateAsync({ ref, body, target: commentTarget })
      .then(() => {
        toast.success('Comment added.');
        setCommentTarget(null);
      })
      .catch((e: unknown) => {
        toastMutationError(e, 'Comment failed');
        throw e;
      });

  const postReply = (body: string) =>
    replyM
      .mutateAsync({ ref, body, target: commentTarget })
      .then(() => {
        toast.success('Reply added.');
        setCommentTarget(null);
      })
      .catch((e: unknown) => {
        toastMutationError(e, 'Reply failed');
        throw e;
      });

  // The market is a key FACT, so it lives in the identity row below with the document number
  // and version — naming it in the header too said the same thing twice on one screen.
  const heroSubtitle = typeLabel;

  // The discussion composer for the current user — a reviewer comments, an editor replies
  // (a dual/admin uses the comment box). Rendered inside the Discussion drawer.
  const threadComposer = mayReview ? (
    <ThreadComposer
      draftRef={threadDraft}
      name={me.data?.name ?? me.data?.email ?? null}
      onSubmit={postComment}
      pending={comment.isPending}
      target={commentTarget}
      onClearTarget={() => setCommentTarget(null)}
    />
  ) : canEdit ? (
    <ThreadComposer
      draftRef={threadDraft}
      name={me.data?.name ?? me.data?.email ?? null}
      onSubmit={postReply}
      pending={replyM.isPending}
      target={commentTarget}
      onClearTarget={() => setCommentTarget(null)}
    />
  ) : null;

  return (
    <VaultDocumentPage
      title={productName}
      subtitle={heroSubtitle}
      back={{ ref }}
      aside={inReview ? <HowReviewWorks canReview={canReview} isProse={isProse} /> : undefined}
      identity={detail.data ? {
        state: detail.data.display_state,
        fields: documentIdentityFields({
          documentNumber: detail.data.document_number,
          versionLabel: draft?.version_label,
          country,
        }),
      } : null}
    >
        {/* The page is about the open working copy, not only about a copy awaiting a decision:
            after a send-back it is still the place to read the changes and talk to the editor.
            Only a document with NO open copy has nothing to show. */}
        {!versions.isPending && !mayReadCopy ? (
          <div className="rounded-2xl border border-line bg-white px-6 py-16 text-center shadow-card">
            <p className="font-medium text-navy">Nothing to review</p>
            <p className="mx-auto mt-1 max-w-md text-[13px] text-muted">
              {hasOpenCopy
                ? `${draft?.created_by ?? 'An editor'} is still working on it — you'll see the `
                  + 'changes here when they submit them for review.'
                : 'There is no open working copy for this document — the released version is '
                  + 'current.'}
            </p>
            <Button className="mt-4" variant="outline" onClick={goBack}>Back to document</Button>
          </div>
        ) : (
          <>
            {/* Who has this working copy + their reason for change — shown to the reviewer and
                the editor alike, styled like a thread entry.

                The verb follows the COPY's state, not the reader's role. It used to read
                "Submitted for review by <the editor>" from the moment anybody checked out, which
                was false until they actually submitted — and a reviewer was shown it over work
                still being written (2026-10-09). An editor or admin may still read an
                unsubmitted copy, so hiding the page from reviewers does not settle this. */}
            {draft && (draft.last_edited_by || draft.reason_for_change) && (
              <div className="mb-5 flex gap-3 rounded-xl border border-line bg-white px-5 py-4 shadow-card">
                <ThreadAvatar name={draft.last_edited_by} />
                <div className="min-w-0">
                  <p className="text-[13px] text-navy">
                    {inReview ? 'Submitted for review' : 'Checked out'}
                    {draft.last_edited_by && (
                      <> by <span className="font-semibold">{draft.last_edited_by}</span></>
                    )}
                  </p>
                  {draft.reason_for_change && (
                    <div className="mt-1">
                      <p className="text-[13px] font-medium text-navy">Reason for change</p>
                      {/* Capped at 2,000 characters, which is still a screenful — show the first
                          few lines so the changes under it stay above the fold. */}
                      <ClampedText className="text-[13px] leading-relaxed text-muted" lines={4}>
                        {draft.reason_for_change}
                      </ClampedText>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* The changes get the full width — the Word page needs it. The discussion lives in
                an on-demand slide-over, opened from the ONE "Discussion" button in the sticky
                action bar (always visible, beside the other review actions). */}
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              {/* No number here. `entries` counts what the COMPARISON found — for an uploaded
                  document that is always 1, "the file changed" — while the rail beside it counts
                  tracked changes. Both were printed as "changes" on one screen, so a reviewer
                  read "Proposed changes (1)" above "3 of 3 changes reviewed" and could not tell
                  which to believe. The rail does the counting; this is a heading (2026-10-09). */}
              <h3 className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted">
                Proposed changes
              </h3>
              {commentCount > 0 && (
                <p className="text-[12px] text-state-warning-text">
                  {commentCount} comment{commentCount === 1 ? '' : 's'} for the editor
                </p>
              )}
            </div>

            {diff.isPending ? (
              <p className="rounded-2xl border border-line bg-white px-5 py-10 text-center text-[13px] text-muted shadow-card">
                Loading changes…
              </p>
            ) : diff.isError ? (
              <p className="rounded-2xl border border-line bg-white px-5 py-10 text-center text-[13px] text-state-error-text shadow-card" role="alert">
                Couldn&rsquo;t load the changes. Check your connection and try again — nothing has
                been approved or sent back.
              </p>
            ) : noChanges ? (
              <div className="rounded-2xl border border-state-warning-border bg-state-warning-bg/50 px-5 py-6 text-center">
                <p className="font-medium text-state-warning-text">No changes from the released version</p>
                <p className="mx-auto mt-1 max-w-md text-[13px] text-state-warning-text/90">
                  This working copy is identical to {released?.label ?? 'the released version'} — there&apos;s
                  nothing to approve. Send it back so the editor can make changes (or fix it in the editor).
                </p>
              </div>
            ) : isDocx && draft ? (
              <Suspense fallback={
                <p className="rounded-2xl border border-line bg-white px-5 py-10 text-center text-[13px] text-muted shadow-card">
                  Loading the changes…
                </p>
              }>
                <DocxRedline
                  refId={ref}
                  versionId={draft.version_id}
                  review={(canReview || canEdit) ? {
                    page: decisionsPage,
                    // Decisions belong to a copy that is IN review; between rounds the list is
                    // read-only rather than offering buttons the server would refuse.
                    canDecide: mayReview && inReview,
                    pending: decide.isPending,
                    commentCounts: commentCounts,
                    onDecide: decideChange,
                    onComment: (entry) => {
                      // A structural change (a joined paragraph, a table row) has no words —
                      // name its kind instead of quoting an empty string.
                      setCommentTarget({
                        revisionId: entry.revisionId,
                        changeText: changePreview(entry).trim() || changeLabel(entry.type),
                      });
                      setDiscussionOpen(true);
                    },
                  } : undefined}
                />
              </Suspense>
            ) : (
              <VersionDiff
                entries={entries}
                releasedLabel={`Current${released ? ` · ${released.label}` : ''}`}
                review={{
                  commentCounts: fieldCommentCounts,
                  onComment: (path, label) => {
                    setCommentTarget({ revisionId: null, changeText: label || path });
                    setDiscussionOpen(true);
                  },
                }}
                files={fileLinks}
              />
            )}

            {canReview && !inReview && showThread && (
              // Sent back and being reworked: no decisions to make, but the discussion stays open.
              <div className="sticky bottom-4 z-20 mt-8 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-line bg-white/95 px-5 py-3 shadow-hero backdrop-blur">
                <span className="text-[13px] text-muted">
                  With the editor for changes — you&apos;ll review it again when it&apos;s resubmitted.
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-muted hover:text-navy"
                  onClick={() => setDiscussionOpen(true)}
                >
                  <MessageSquare className="size-4" /> Discussion
                  {thread.length > 0 ? ` (${thread.length})` : ''}
                </Button>
              </div>
            )}

            {canReview && inReview && (
              <>
                {/* Sticky decision bar — Approve / Send back stay in reach without scrolling to
                    the bottom of a long redline (mirrors the edit page's sticky action bar). */}
                <div className="sticky bottom-4 z-20 mt-8 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-line bg-white/95 px-5 py-3 shadow-hero backdrop-blur">
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px]">
                    {noChanges ? (
                      <span className="text-state-warning-text">
                        No changes from {released?.label ?? 'the released version'} — nothing to approve.
                      </span>
                    ) : (
                      <>
                        <span className={cn('font-medium', ownEdit ? 'text-state-warning-text' : 'text-navy')}>
                          {ownEdit
                            ? OWN_EDIT_NOTE
                            : progress && progress.total > 0
                              ? `${progress.accepted + progress.rejected} of ${progress.total} changes reviewed`
                              : 'Approve to release, or send back for changes.'}
                        </span>
                        {progress && progress.rejected > 0 && (
                          <>
                            <span className="hidden text-line-strong sm:inline">·</span>
                            <span className="text-state-error-text">
                              {progress.rejected} rejected
                            </span>
                          </>
                        )}
                        {commentCount > 0 && (
                          <>
                            <span className="hidden text-line-strong sm:inline">·</span>
                            <span className="text-state-warning-text">
                              {commentCount} comment{commentCount === 1 ? '' : 's'} for the editor
                            </span>
                          </>
                        )}
                      </>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {showThread && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-muted hover:text-navy"
                        onClick={() => setDiscussionOpen(true)}
                      >
                        <MessageSquare className="size-4" /> Discussion
                        {thread.length > 0 ? ` (${thread.length})` : ''}
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-state-warning-border text-state-warning-text hover:bg-state-warning-bg"
                      disabled={ownEdit}
                      title={ownEdit ? OWN_EDIT_NOTE : undefined}
                      onClick={() => setSendBackOpen(true)}
                    >
                      <Undo2 className="size-4" /> Send back{rejectedDecisions.length > 0 ? ` (${rejectedDecisions.length} rejected)` : ''}
                    </Button>
                    <Button
                      size="sm"
                      disabled={noChanges || decisionsBlockApproval || ownEdit}
                      title={ownEdit ? OWN_EDIT_NOTE : noChanges ? 'No changes to approve' : approvalBlocker ?? undefined}
                      onClick={() => setApproveOpen(true)}
                    >
                      <ShieldCheck className="size-4" /> Approve &amp; release
                    </Button>
                  </div>
                </div>

                {/* Approve: a focused e-signature step stating the outcome + the signer. */}
                <Dialog open={approveOpen} onOpenChange={setApproveOpen}>
                  <SizedDialog size="form">
                    <DialogHeader>
                      <DialogTitle>Approve &amp; release — electronic signature</DialogTitle>
                      <DialogDescription>
                        Approving e-signs this working copy and releases it as the next version. This is
                        your legally binding signature under 21 CFR Part 11.
                      </DialogDescription>
                    </DialogHeader>
                    {/* What is being signed, as facts to scan rather than a paragraph to parse:
                        the version going out, and the changes the signature covers. */}
                    <dl className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2 rounded-lg border border-line bg-line-soft/40 px-4 py-3">
                      <div className="min-w-0">
                        <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted">Releasing</dt>
                        <dd className="mt-0.5 text-[13px] text-navy">
                          {draft?.version_label ? (
                            <span className="font-mono font-semibold">{draft.version_label}</span>
                          ) : (
                            'this working copy'
                          )}
                          {released && <span className="ml-1.5 text-[12px] text-muted">replaces {released.label}</span>}
                        </dd>
                      </div>
                      <div className="min-w-0">
                        <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted">
                          {progress && progress.total > 0 ? 'Changes you accepted' : 'What is changing'}
                        </dt>
                        <dd className="mt-0.5 text-[13px] text-navy">
                          {progress && progress.total > 0 ? (
                            <>
                              <span className="font-semibold">{progress.accepted}</span> of {progress.total}
                            </>
                          ) : (
                            // A PDF, a video or a spreadsheet carries no change model — you cannot
                            // diff two videos — so the change IS the replacement. Say that, rather
                            // than "no tracked changes", which tells the signer nothing.
                            <span className="[overflow-wrap:anywhere]">{revisionSummaryText}</span>
                          )}
                        </dd>
                      </div>
                    </dl>
                    <div className="space-y-1.5">
                      <label htmlFor="approve-reason" className="text-[13px] font-medium text-navy">
                        Reason for approving<span className="ml-0.5 text-error" aria-hidden>*</span>
                      </label>
                      {/* maxLength matches ApproveRequest.reason (COLLAB_MESSAGE_MAX): the browser stops at the
                          limit rather than letting a paste bounce off the server as a 422. This
                          text lands in an immutable audit row, so it is bounded on both sides. */}
                      <textarea
                        id="approve-reason"
                        rows={4}
                        value={approveReason}
                        onChange={(e) => setApproveReason(e.target.value)}
                        placeholder="e.g. Reviewed for regulatory accuracy."
                        maxLength={COLLAB_MESSAGE_MAX}
                        className="w-full resize-y rounded-md border border-line px-3 py-2 text-[13px]"
                      />
                      <CharacterCount value={approveReason} max={COLLAB_MESSAGE_MAX} />
                    </div>
                    <div className="space-y-1.5">
                      <label htmlFor="approve-pw" className="text-[13px] font-medium text-navy">
                        Password (e-signature)<span className="ml-0.5 text-error" aria-hidden>*</span>
                      </label>
                      <input
                        id="approve-pw"
                        type="password"
                        autoComplete="current-password"
                        autoFocus
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full rounded-md border border-line px-3 py-2 text-[13px]"
                      />
                      <p className="text-[12px] text-muted">
                        Signing as{' '}
                        <span className="font-medium text-navy">
                          {me.data?.name ?? me.data?.email ?? 'you'}
                        </span>
                        .
                      </p>
                    </div>
                    {commentCount > 0 && (
                      <p className="rounded-md bg-state-warning-bg/50 px-3 py-2 text-[12.5px] text-state-warning-text">
                        You have {commentCount} comment{commentCount === 1 ? '' : 's'} — approving releases
                        the copy as-is and discards them. To send them to the editor, use Send back.
                      </p>
                    )}
                    <DialogFooter>
                      <Button variant="ghost" onClick={() => setApproveOpen(false)} disabled={approve.isPending}>
                        Cancel
                      </Button>
                      <Button onClick={approveNow} disabled={!canApprove}>
                        {approve.isPending ? 'Signing…' : 'Approve & sign'}
                      </Button>
                    </DialogFooter>
                  </SizedDialog>
                </Dialog>

                {/* Send back: an overall note (+ any per-change comments) returns the copy to the editor. */}
                <Dialog open={sendBackOpen} onOpenChange={setSendBackOpen}>
                  <SizedDialog size="form">
                    <DialogHeader>
                      <DialogTitle>Send back to the editor</DialogTitle>
                      <DialogDescription>
                        Returns the working copy to the editor to revise — nothing is released.
                        {commentCount > 0 ? ' Your per-change comments go with it.' : ''}
                      </DialogDescription>
                    </DialogHeader>
                    {/* What the send-back DOES to the copy, as facts to scan — the same shape as
                        the approve dialog, so the two decisions read alike. */}
                    {(!progress || progress.total === 0) && (
                      <p className="rounded-lg border border-line bg-line-soft/40 px-4 py-3 text-[13px] text-navy">
                        <span className="text-muted">Going back: </span>
                        <span className="[overflow-wrap:anywhere]">{revisionSummaryText}</span>
                      </p>
                    )}
                    {progress && progress.total > 0 && (
                      <dl className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2 rounded-lg border border-line bg-line-soft/40 px-4 py-3">
                        <div className="min-w-0">
                          <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted">Accepted</dt>
                          <dd className="mt-0.5 text-[13px] text-navy">
                            <span className="font-semibold">{progress.accepted}</span>
                            <span className="ml-1.5 text-[12px] text-muted">applied to the copy</span>
                          </dd>
                        </div>
                        <div className="min-w-0">
                          <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted">To rework</dt>
                          <dd className="mt-0.5 text-[13px] text-navy">
                            <span className="font-semibold">{progress.rejected}</span>
                            <span className="ml-1.5 text-[12px] text-muted">
                              {progress.rejected === 0 ? 'nothing rejected' : 'reverted'}
                            </span>
                          </dd>
                        </div>
                        {progress.open > 0 && (
                          <div className="col-span-2 min-w-0 border-t border-line pt-2.5">
                            <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted">Left open</dt>
                            <dd className="mt-0.5 text-[13px] text-navy">
                              <span className="font-semibold">{progress.open}</span>
                              <span className="ml-1.5 text-[12px] text-muted">
                                still marked in the copy — the editor sees them as proposals
                              </span>
                            </dd>
                          </div>
                        )}
                      </dl>
                    )}
                    {rejectedDecisions.length > 0 && (
                      <div className="rounded-lg border border-line bg-white px-4 py-3">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-muted">
                          Going back to be reworked
                        </p>
                        <ul className="mt-2 max-h-40 space-y-1.5 overflow-y-auto">
                          {rejectedDecisions.map((d) => (
                            <li key={d.revision_id} className="flex gap-2 text-[13px] text-navy">
                              <span className="shrink-0 text-muted">
                                {changeLabel(d.change_type).toLowerCase()}
                                {isPictureChange({ text: d.change_text }) ? ' a picture' : ''}
                              </span>
                              <span className="min-w-0 [overflow-wrap:anywhere]">&ldquo;{d.change_text}&rdquo;</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    <div className="space-y-1.5">
                      <label htmlFor="sendback-reason" className="text-[13px] font-medium text-navy">
                        What should the editor fix?<span className="ml-0.5 text-error" aria-hidden>*</span>
                      </label>
                      <textarea
                        id="sendback-reason"
                        value={sendBackReason}
                        onChange={(e) => setSendBackReason(e.target.value)}
                        rows={4}
                        autoFocus
                        maxLength={COLLAB_MESSAGE_MAX}
                        placeholder="e.g. The intended-use wording needs to match section 2."
                        className="w-full resize-y rounded-md border border-line px-3 py-2 text-[13px] leading-relaxed"
                      />
                      <CharacterCount value={sendBackReason} max={COLLAB_MESSAGE_MAX} />
                    </div>
                    <DialogFooter>
                      <Button variant="ghost" onClick={() => setSendBackOpen(false)} disabled={reject.isPending}>
                        Cancel
                      </Button>
                      <Button variant="outline" onClick={sendBack} disabled={!canSendBack}>
                        {reject.isPending ? 'Sending…' : `Send back${commentCount > 0 ? ` (${commentCount})` : ''}`}
                      </Button>
                    </DialogFooter>
                  </SizedDialog>
                </Dialog>
              </>
            )}

            {/* Discussion slide-over — one click from the sticky bar,
                and it never steals width from the document. Available to reviewer and editor. */}
            {showThread && (
              <DiscussionSheet
                open={discussionOpen}
                onOpenChange={setDiscussionOpen}
                entries={thread}
                currentEmail={me.data?.email ?? null}
                composer={threadComposer}
              />
            )}
          </>
        )}
    </VaultDocumentPage>
  );
}
